/* ==========================================================
Great Reminders — Strategic Network System (Vanilla JS)
Purpose:
You don’t need 1,000 contacts. You need 20–50 high-impact people you engage with intentionally.
This tool helps you build a strategic map of the people shaping your future — with clear roles,
context notes, reflections, and purposeful next actions (NOT a CRM, NOT a feed).

Key concepts:
- Top 20–50: intentional, high-leverage relationships
- Nodes (Strategic Roles): Mentor, Sponsor, Peer, Team, Client, Inspiration
- Categories (Impact Levers): Career, Learning, Business, Energy, Perspective
- Person Card: role + context + interaction timeline + reflections + next action
- Rituals: quarterly review + balance view

Minimal HTML skeleton you need (example):
------------------------------------------------------------
<div class="sn-wrap">
  <div class="sn-card">
    <div class="sn-header">
      <h2 class="sn-title">Strategic Network Map</h2>
      <p class="sn-subtitle">
        You don’t need 1,000 contacts — focus on 20–50 high-impact people.
        Build a strategic map, capture context, and take intentional actions.
      </p>
    </div>

    <div class="sn-grid">
      <section class="sn-section">
        <h3 class="sn-h3">Add person</h3>

        <div class="sn-row">
          <label class="sn-label">Name</label>
          <input id="sn_name" class="sn-input" placeholder="e.g., Sarah Jacobs" />
        </div>

        <div class="sn-row">
          <label class="sn-label">LinkedIn URL (optional)</label>
          <input id="sn_linkedin" class="sn-input" placeholder="https://linkedin.com/in/..." />
        </div>

        <div class="sn-row">
          <label class="sn-label">Node (strategic role)</label>
          <select id="sn_node" class="sn-input"></select>
        </div>

        <div class="sn-row">
          <label class="sn-label">Category (impact lever)</label>
          <select id="sn_category" class="sn-input"></select>
        </div>

        <div class="sn-row">
          <label class="sn-label">Why they matter (1 line)</label>
          <input id="sn_why" class="sn-input" placeholder="e.g., Helps me think bigger; connects me to operators in X." />
        </div>

        <div class="sn-row">
          <label class="sn-label">What do they care about?</label>
          <input id="sn_care" class="sn-input" placeholder="e.g., team performance, scaling systems, great talent..." />
        </div>

        <div class="sn-row">
          <label class="sn-label">How can I add value?</label>
          <input id="sn_value" class="sn-input" placeholder="e.g., Share a template; intro; amplify their post; offer feedback." />
        </div>

        <div class="sn-row">
          <label class="sn-label">Energy</label>
          <select id="sn_energy" class="sn-input">
            <option value="energizing">🔋 Energizing</option>
            <option value="neutral">😐 Neutral</option>
            <option value="draining">🔻 Draining</option>
          </select>
        </div>

        <div class="sn-actions">
          <button id="sn_add" class="sn-btn sn-btn-primary">Add to Top Network</button>
          <button id="sn_clear" class="sn-btn sn-btn-ghost">Clear</button>
        </div>

        <p class="sn-help">
          Tip: Keep this list to ~20–50 people. If it grows, prune intentionally during your quarterly review.
        </p>
      </section>

      <section class="sn-section">
        <h3 class="sn-h3">Your Top Network</h3>

        <div class="sn-filters">
          <input id="sn_search" class="sn-input" placeholder="Search name, note, node, category..." />
          <select id="sn_filterNode" class="sn-input"></select>
          <select id="sn_filterCategory" class="sn-input"></select>
          <select id="sn_sort" class="sn-input">
            <option value="recent">Most recently interacted</option>
            <option value="name">Name A→Z</option>
            <option value="energy">Energy (🔋 → 🔻)</option>
            <option value="priority">Priority score</option>
          </select>
        </div>

        <div id="sn_list" class="sn-list"></div>
      </section>

      <section class="sn-section">
        <h3 class="sn-h3">Balance View</h3>
        <div id="sn_balance" class="sn-balance"></div>

        <div class="sn-divider"></div>

        <h3 class="sn-h3">Quarterly Review (5 min)</h3>
        <div class="sn-actions">
          <button id="sn_quarterly" class="sn-btn">Generate Review Prompts</button>
          <button id="sn_export" class="sn-btn">Export JSON</button>
          <button id="sn_reset" class="sn-btn sn-btn-ghost">Reset All</button>
        </div>
        <pre id="sn_review" class="sn-pre"></pre>
      </section>
    </div>

    <!-- Slide-over / modal for person details -->
    <div id="sn_modal" class="sn-modal" aria-hidden="true">
      <div class="sn-modal-card">
        <div class="sn-modal-head">
          <div>
            <h3 id="sn_m_name" class="sn-h3"></h3>
            <p id="sn_m_meta" class="sn-meta"></p>
          </div>
          <button id="sn_m_close" class="sn-btn sn-btn-ghost">Close</button>
        </div>

        <div class="sn-modal-grid">
          <section class="sn-modal-section">
            <h4 class="sn-h4">Strategic context</h4>
            <p><strong>Why they matter:</strong> <span id="sn_m_why"></span></p>
            <p><strong>They care about:</strong> <span id="sn_m_care"></span></p>
            <p><strong>I can add value by:</strong> <span id="sn_m_value"></span></p>
            <p><strong>Energy:</strong> <span id="sn_m_energy"></span></p>
            <p><strong>LinkedIn:</strong> <a id="sn_m_link" target="_blank" rel="noopener">Open</a></p>
          </section>

          <section class="sn-modal-section">
            <h4 class="sn-h4">Interaction</h4>

            <label class="sn-label">Log an interaction</label>
            <select id="sn_m_type" class="sn-input">
              <option value="conversation">Conversation</option>
              <option value="feedback">Feedback</option>
              <option value="help_received">Help received</option>
              <option value="help_given">Help given</option>
              <option value="reconnect">Reconnect</option>
            </select>

            <label class="sn-label">Note (what mattered?)</label>
            <textarea id="sn_m_note" class="sn-input" rows="3" placeholder="Capture the meaning, not the transcript..."></textarea>

            <div class="sn-actions">
              <button id="sn_m_log" class="sn-btn sn-btn-primary">Log</button>
              <button id="sn_m_delete" class="sn-btn">Delete person</button>
            </div>

            <h4 class="sn-h4">Timeline</h4>
            <div id="sn_m_timeline" class="sn-timeline"></div>
          </section>

          <section class="sn-modal-section">
            <h4 class="sn-h4">Reflection (your advantage)</h4>
            <p class="sn-help">This is what makes it different from LinkedIn/CRMs: leadership-level insight.</p>
            <div class="sn-actions sn-actions-wrap" id="sn_m_reflectBtns"></div>

            <label class="sn-label">Reflection note</label>
            <textarea id="sn_m_reflectNote" class="sn-input" rows="3" placeholder="What’s the real next move? What are you avoiding?"></textarea>

            <div class="sn-actions">
              <button id="sn_m_saveReflect" class="sn-btn">Save reflection</button>
            </div>
          </section>

          <section class="sn-modal-section">
            <h4 class="sn-h4">Next intentional action</h4>
            <label class="sn-label">Intent</label>
            <select id="sn_m_intent" class="sn-input"></select>

            <label class="sn-label">Next action (one sentence)</label>
            <input id="sn_m_next" class="sn-input" placeholder="e.g., Thank them for X + share the 1-page template." />

            <label class="sn-label">Due date (optional)</label>
            <input id="sn_m_due" class="sn-input" type="date" />

            <div class="sn-actions">
              <button id="sn_m_saveNext" class="sn-btn sn-btn-primary">Save next action</button>
              <button id="sn_m_copy" class="sn-btn">Copy summary</button>
            </div>

            <pre id="sn_m_summary" class="sn-pre"></pre>
          </section>
        </div>
      </div>
    </div>
  </div>
</div>
------------------------------------------------------------

========================================================== */

(() => {
  "use strict";

  // -----------------------------
  // Config
  // -----------------------------
  const STORAGE_KEY = "sn_state_v1";

  const NODES = [
    { id: "mentor", name: "Mentor", hint: "Accelerates your thinking & skills." },
    { id: "sponsor", name: "Sponsor", hint: "Opens doors, advocates for you." },
    { id: "peer", name: "Peer", hint: "Mutual growth, shared reality." },
    { id: "team", name: "Team", hint: "People you lead / work with daily." },
    { id: "client", name: "Client", hint: "Value exchange & opportunities." },
    { id: "inspiration", name: "Inspiration", hint: "Raises your standards & perspective." }
  ];

  const CATEGORIES = [
    { id: "career", name: "Career acceleration" },
    { id: "learning", name: "Learning & mastery" },
    { id: "business", name: "Business opportunities" },
    { id: "energy", name: "Energy & wellbeing" },
    { id: "perspective", name: "Perspective & clarity" }
  ];

  const INTENTS = [
    { id: "learn_from", name: "Learn from" },
    { id: "thank", name: "Thank" },
    { id: "reconnect", name: "Reconnect" },
    { id: "ask_perspective", name: "Ask perspective" },
    { id: "offer_support", name: "Offer support" }
  ];

  const REFLECTION_PROMPTS = [
    "How does this person stretch my thinking?",
    "What would ‘adding value’ look like here?",
    "What am I avoiding in this relationship?",
    "What’s the next courageous action?",
    "If I could only keep 10 people, would they be in it? Why?",
    "How can I help them win this quarter?"
  ];

  const ENERGY_SCORE = { energizing: 3, neutral: 2, draining: 1 };
  const ENERGY_LABEL = { energizing: "🔋 Energizing", neutral: "😐 Neutral", draining: "🔻 Draining" };

  // -----------------------------
  // Helpers
  // -----------------------------
  const $ = (id) => document.getElementById(id);

  const nowISO = () => new Date().toISOString();

  function safeText(v) {
    return (v ?? "").toString().trim();
  }

  function validUrl(url) {
    if (!url) return "";
    try {
      const u = new URL(url);
      return u.protocol.startsWith("http") ? u.toString() : "";
    } catch {
      return "";
    }
  }

  function fmtDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return "—";
    }
  }

  function escapeHtml(str) {
    return (str ?? "")
      .toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied");
    } catch {
      toast("Copy failed — copy manually");
    }
  }

  let toastTimer = null;
  function toast(message) {
    let el = document.querySelector(".sn-toast");
    if (!el) {
      el = document.createElement("div");
      el.className = "sn-toast";
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add("is-visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("is-visible"), 1600);
  }

  // -----------------------------
  // State
  // -----------------------------
  const state = {
    people: [], // [{id, name, linkedin, node, category, why, care, value, energy, createdAt, interactions:[], reflections:[], nextAction:{...}}]
    ui: { search: "", filterNode: "all", filterCategory: "all", sort: "recent" },
    selectedId: null
  };

  function uid() {
    // Simple uid for local usage
    return "p_" + Math.random().toString(16).slice(2) + "_" + Date.now().toString(16);
  }

  function save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  function load() {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        state.people = Array.isArray(parsed.people) ? parsed.people : [];
        state.ui = parsed.ui || state.ui;
        state.selectedId = parsed.selectedId || null;
      }
    } catch {
      // ignore
    }
  }

  function resetAll() {
    localStorage.removeItem(STORAGE_KEY);
    location.reload();
  }

  // -----------------------------
  // Rendering: selects
  // -----------------------------
  function fillSelect(el, items, { includeAll = false, allLabel = "All", allValue = "all" } = {}) {
    if (!el) return;
    el.innerHTML = "";
    if (includeAll) {
      const opt = document.createElement("option");
      opt.value = allValue;
      opt.textContent = allLabel;
      el.appendChild(opt);
    }
    items.forEach((it) => {
      const opt = document.createElement("option");
      opt.value = it.id;
      opt.textContent = it.name;
      el.appendChild(opt);
    });
  }

  function nodeName(id) {
    return NODES.find((n) => n.id === id)?.name || "—";
  }
  function categoryName(id) {
    return CATEGORIES.find((c) => c.id === id)?.name || "—";
  }

  // -----------------------------
  // Add person
  // -----------------------------
  function readAddForm() {
    return {
      name: safeText($("sn_name")?.value),
      linkedin: validUrl(safeText($("sn_linkedin")?.value)),
      node: $("sn_node")?.value || "peer",
      category: $("sn_category")?.value || "career",
      why: safeText($("sn_why")?.value),
      care: safeText($("sn_care")?.value),
      value: safeText($("sn_value")?.value),
      energy: $("sn_energy")?.value || "neutral"
    };
  }

  function clearAddForm() {
    if ($("sn_name")) $("sn_name").value = "";
    if ($("sn_linkedin")) $("sn_linkedin").value = "";
    if ($("sn_why")) $("sn_why").value = "";
    if ($("sn_care")) $("sn_care").value = "";
    if ($("sn_value")) $("sn_value").value = "";
    if ($("sn_energy")) $("sn_energy").value = "energizing";
    if ($("sn_node")) $("sn_node").value = "peer";
    if ($("sn_category")) $("sn_category").value = "career";
  }

  function addPerson() {
    const form = readAddForm();
    if (!form.name) {
      toast("Name is required");
      return;
    }

    if (state.people.length >= 50) {
      toast("You’re at 50. Prune in your quarterly review before adding more.");
      return;
    }

    const person = {
      id: uid(),
      createdAt: nowISO(),
      name: form.name,
      linkedin: form.linkedin,
      node: form.node,
      category: form.category,
      why: form.why,
      care: form.care,
      value: form.value,
      energy: form.energy,
      interactions: [],
      reflections: [],
      nextAction: { intent: "reconnect", text: "", due: "" }
    };

    state.people.unshift(person);
    save();
    clearAddForm();
    render();
    toast("Added");
  }

  // -----------------------------
  // List + filtering
  // -----------------------------
  function lastInteractionDate(person) {
    const last = person.interactions?.[0];
    return last?.at || person.createdAt;
  }

  function priorityScore(person) {
    // Not a “truth score”, just a sorting helper to surface good bets:
    // energy (1..3) + recency boost + if next action exists
    const energy = ENERGY_SCORE[person.energy] || 2;

    const last = new Date(lastInteractionDate(person)).getTime();
    const daysAgo = Math.max(0, (Date.now() - last) / (1000 * 60 * 60 * 24));
    const recency = Math.max(0, 3 - Math.min(3, daysAgo / 30)); // 0..3 within ~90 days

    const hasNext = person.nextAction?.text ? 1 : 0;
    const hasWhy = person.why ? 1 : 0;

    return energy + recency + hasNext + hasWhy;
  }

  function filteredPeople() {
    let list = [...state.people];

    const q = safeText(state.ui.search).toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const blob = [
          p.name,
          p.linkedin,
          nodeName(p.node),
          categoryName(p.category),
          p.why,
          p.care,
          p.value,
          p.energy,
          p.nextAction?.text,
          p.reflections?.map((r) => r.note).join(" "),
          p.interactions?.map((i) => i.note).join(" ")
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }

    if (state.ui.filterNode !== "all") {
      list = list.filter((p) => p.node === state.ui.filterNode);
    }
    if (state.ui.filterCategory !== "all") {
      list = list.filter((p) => p.category === state.ui.filterCategory);
    }

    const sort = state.ui.sort;
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "recent") list.sort((a, b) => new Date(lastInteractionDate(b)) - new Date(lastInteractionDate(a)));
    if (sort === "energy") list.sort((a, b) => (ENERGY_SCORE[b.energy] || 2) - (ENERGY_SCORE[a.energy] || 2));
    if (sort === "priority") list.sort((a, b) => priorityScore(b) - priorityScore(a));

    return list;
  }

  function renderList() {
    const el = $("sn_list");
    if (!el) return;

    const list = filteredPeople();
    if (!list.length) {
      el.innerHTML = `<div class="sn-empty">
        <p><strong>No people yet.</strong></p>
        <p>Add 20–50 high-impact people and treat this as your strategic map — not a contact dump.</p>
      </div>`;
      return;
    }

    el.innerHTML = "";

    list.forEach((p) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "sn-person" + (p.id === state.selectedId ? " is-active" : "");
      card.addEventListener("click", () => openModal(p.id));

      const last = fmtDate(lastInteractionDate(p));
      const next = p.nextAction?.text ? escapeHtml(p.nextAction.text) : "<em>—</em>";

      card.innerHTML = `
        <div class="sn-person-top">
          <div class="sn-person-name">${escapeHtml(p.name)}</div>
          <div class="sn-person-energy">${ENERGY_LABEL[p.energy] || "😐 Neutral"}</div>
        </div>
        <div class="sn-person-meta">
          <span class="sn-pill">${escapeHtml(nodeName(p.node))}</span>
          <span class="sn-pill sn-pill-soft">${escapeHtml(categoryName(p.category))}</span>
          <span class="sn-pill sn-pill-ghost">Last: ${escapeHtml(last)}</span>
        </div>
        <div class="sn-person-next">
          <strong>Next:</strong> ${next}
        </div>
      `;

      el.appendChild(card);
    });
  }

  // -----------------------------
  // Balance view
  // -----------------------------
  function countBy(list, keyFn) {
    const m = new Map();
    list.forEach((x) => {
      const k = keyFn(x);
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }

  function renderBalance() {
    const el = $("sn_balance");
    if (!el) return;

    const total = state.people.length;
    const byNode = countBy(state.people, (p) => p.node);
    const byCat = countBy(state.people, (p) => p.category);
    const byEnergy = countBy(state.people, (p) => p.energy);

    const line = (title, entries, mapFn) => {
      const rows = entries
        .map(([k, v]) => `<div class="sn-b-row"><span>${escapeHtml(mapFn(k))}</span><strong>${v}</strong></div>`)
        .join("");
      return `<div class="sn-b-block">
        <div class="sn-b-title">${escapeHtml(title)}</div>
        ${rows || `<div class="sn-b-row"><span>—</span><strong>0</strong></div>`}
      </div>`;
    };

    el.innerHTML = `
      <div class="sn-b-head">
        <p><strong>Total:</strong> ${total} ${total ? "(aim for ~20–50)" : ""}</p>
        <p class="sn-help">Leadership insight: balance your network between growth (mentors), momentum (peers/operators), and contribution (people you support).</p>
      </div>
      <div class="sn-b-grid">
        ${line("Roles (Nodes)", byNode, nodeName)}
        ${line("Impact (Categories)", byCat, categoryName)}
        ${line("Energy", byEnergy, (k) => ENERGY_LABEL[k] || k)}
      </div>
    `;
  }

  // -----------------------------
  // Modal (person card)
  // -----------------------------
  function getPerson(id) {
    return state.people.find((p) => p.id === id);
  }

  function setModalOpen(open) {
    const modal = $("sn_modal");
    if (!modal) return;
    modal.setAttribute("aria-hidden", open ? "false" : "true");
    modal.classList.toggle("is-open", !!open);
    if (!open) state.selectedId = null;
    save();
  }

  function openModal(id) {
    const p = getPerson(id);
    if (!p) return;
    state.selectedId = id;
    save();
    renderModal(p);
    setModalOpen(true);
    renderList(); // update active highlight
  }

  function closeModal() {
    setModalOpen(false);
    renderList();
  }

  function renderTimeline(p) {
    const el = $("sn_m_timeline");
    if (!el) return;

    const items = (p.interactions || []).slice(0, 12);
    if (!items.length) {
      el.innerHTML = `<div class="sn-empty"><p>No interactions yet. Log meaningful moments (not transcripts).</p></div>`;
      return;
    }

    el.innerHTML = items
      .map((it) => {
        return `<div class="sn-time-item">
          <div class="sn-time-top">
            <strong>${escapeHtml(it.type.replaceAll("_", " "))}</strong>
            <span class="sn-time-date">${escapeHtml(fmtDate(it.at))}</span>
          </div>
          <div class="sn-time-note">${escapeHtml(it.note || "")}</div>
        </div>`;
      })
      .join("");
  }

  function renderReflectBtns() {
    const box = $("sn_m_reflectBtns");
    if (!box) return;
    box.innerHTML = "";
    REFLECTION_PROMPTS.forEach((p) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sn-btn sn-btn-chip";
      btn.textContent = p;
      btn.addEventListener("click", () => {
        const ta = $("sn_m_reflectNote");
        if (!ta) return;
        const existing = safeText(ta.value);
        ta.value = existing ? `${existing}\n- ${p}` : `- ${p}`;
        ta.focus();
      });
      box.appendChild(btn);
    });
  }

  function renderModal(p) {
    if ($("sn_m_name")) $("sn_m_name").textContent = p.name;

    const meta = `${nodeName(p.node)} • ${categoryName(p.category)} • Added ${fmtDate(p.createdAt)}`;
    if ($("sn_m_meta")) $("sn_m_meta").textContent = meta;

    if ($("sn_m_why")) $("sn_m_why").textContent = p.why || "—";
    if ($("sn_m_care")) $("sn_m_care").textContent = p.care || "—";
    if ($("sn_m_value")) $("sn_m_value").textContent = p.value || "—";
    if ($("sn_m_energy")) $("sn_m_energy").textContent = ENERGY_LABEL[p.energy] || "—";

    const linkEl = $("sn_m_link");
    if (linkEl) {
      const u = validUrl(p.linkedin);
      linkEl.href = u || "#";
      linkEl.textContent = u ? "Open profile" : "—";
      linkEl.style.pointerEvents = u ? "auto" : "none";
      linkEl.style.opacity = u ? "1" : "0.6";
    }

    // defaults for interaction logging
    if ($("sn_m_type")) $("sn_m_type").value = "conversation";
    if ($("sn_m_note")) $("sn_m_note").value = "";

    // reflections
    renderReflectBtns();
    if ($("sn_m_reflectNote")) $("sn_m_reflectNote").value = "";

    // next action
    fillSelect($("sn_m_intent"), INTENTS);
    if ($("sn_m_intent")) $("sn_m_intent").value = p.nextAction?.intent || "reconnect";
    if ($("sn_m_next")) $("sn_m_next").value = p.nextAction?.text || "";
    if ($("sn_m_due")) $("sn_m_due").value = p.nextAction?.due || "";

    // summary output
    if ($("sn_m_summary")) $("sn_m_summary").textContent = buildPersonSummary(p);

    // timeline
    renderTimeline(p);
  }

  function logInteraction() {
    const p = getPerson(state.selectedId);
    if (!p) return;

    const type = $("sn_m_type")?.value || "conversation";
    const note = safeText($("sn_m_note")?.value);
    if (!note) {
      toast("Add a short note (what mattered?)");
      return;
    }

    p.interactions = p.interactions || [];
    p.interactions.unshift({ at: nowISO(), type, note });

    save();
    renderModal(p);
    renderList();
    renderBalance();
    toast("Logged");
  }

  function saveReflection() {
    const p = getPerson(state.selectedId);
    if (!p) return;

    const note = safeText($("sn_m_reflectNote")?.value);
    if (!note) {
      toast("Add a reflection note");
      return;
    }

    p.reflections = p.reflections || [];
    p.reflections.unshift({ at: nowISO(), note });

    if ($("sn_m_reflectNote")) $("sn_m_reflectNote").value = "";
    save();
    renderModal(p);
    toast("Saved reflection");
  }

  function saveNextAction() {
    const p = getPerson(state.selectedId);
    if (!p) return;

    const intent = $("sn_m_intent")?.value || "reconnect";
    const text = safeText($("sn_m_next")?.value);
    const due = $("sn_m_due")?.value || "";

    p.nextAction = { intent, text, due };

    save();
    renderModal(p);
    renderList();
    toast("Saved");
  }

  function deletePerson() {
    const id = state.selectedId;
    if (!id) return;

    const p = getPerson(id);
    if (!p) return;

    const ok = confirm(`Delete ${p.name} from your network map?`);
    if (!ok) return;

    state.people = state.people.filter((x) => x.id !== id);
    state.selectedId = null;
    save();
    closeModal();
    render();
    toast("Deleted");
  }

  function buildPersonSummary(p) {
    const last = fmtDate(lastInteractionDate(p));
    const due = p.nextAction?.due ? fmtDate(p.nextAction.due) : "—";
    const next = p.nextAction?.text || "—";
    const lastNote = p.interactions?.[0]?.note || "—";

    return `PERSON CARD SUMMARY
Name: ${p.name}
Role (Node): ${nodeName(p.node)}
Impact (Category): ${categoryName(p.category)}
Energy: ${ENERGY_LABEL[p.energy] || "—"}
LinkedIn: ${p.linkedin || "—"}

Strategic context:
- Why they matter: ${p.why || "—"}
- They care about: ${p.care || "—"}
- I can add value by: ${p.value || "—"}

Interaction:
- Last interaction: ${last}
- Last note: ${lastNote}

Next intentional action:
- Intent: ${INTENTS.find((i) => i.id === (p.nextAction?.intent || ""))?.name || "—"}
- Action: ${next}
- Due: ${due}
`;
  }

  function copyPersonSummary() {
    const p = getPerson(state.selectedId);
    if (!p) return;
    copyText(buildPersonSummary(p));
  }

  // -----------------------------
  // Quarterly review
  // -----------------------------
  function generateQuarterlyReview() {
    const list = [...state.people];
    const topByPriority = list.sort((a, b) => priorityScore(b) - priorityScore(a)).slice(0, 12);

    const energizing = list.filter((p) => p.energy === "energizing").slice(0, 8);
    const draining = list.filter((p) => p.energy === "draining").slice(0, 8);

    const byNode = countBy(list, (p) => p.node).map(([k, v]) => `- ${nodeName(k)}: ${v}`);
    const byCat = countBy(list, (p) => p.category).map(([k, v]) => `- ${categoryName(k)}: ${v}`);

    const prompts = `QUARTERLY NETWORK REVIEW (5 MIN)

1) Who mattered most this quarter?
- Pick 3 names and write 1 sentence: what changed because of them?

2) Who did I neglect but value?
- Choose 3 people you want to reconnect with (small, sincere message).

3) Who no longer belongs in my Top 50?
- Remove 3–5 people that no longer align with your direction.

4) Network balance check
Roles:
${byNode.join("\n") || "- —"}
Impact:
${byCat.join("\n") || "- —"}

5) Energy & integrity
- Energizing: ${energizing.map((p) => p.name).join(", ") || "—"}
- Draining: ${draining.map((p) => p.name).join(", ") || "—"}
Reflection: What boundary or conversation would raise integrity here?

6) Your next 7 days (tiny actions)
- Send 2 thanks
- Reconnect with 2
- Offer value to 1
- Ask perspective from 1

Suggested focus (top priority people):
${topByPriority.map((p) => `- ${p.name} (${nodeName(p.node)} / ${categoryName(p.category)}) — Next: ${p.nextAction?.text || "set a next action"}`).join("\n") || "- —"}
`;

    if ($("sn_review")) $("sn_review").textContent = prompts;
    toast("Review generated");
  }

  function exportJSON() {
    const json = JSON.stringify(state, null, 2);
    copyText(json);
  }

  // -----------------------------
  // Main render
  // -----------------------------
  function render() {
    // sync UI inputs
    if ($("sn_search")) $("sn_search").value = state.ui.search || "";
    if ($("sn_sort")) $("sn_sort").value = state.ui.sort || "recent";
    if ($("sn_filterNode")) $("sn_filterNode").value = state.ui.filterNode || "all";
    if ($("sn_filterCategory")) $("sn_filterCategory").value = state.ui.filterCategory || "all";

    renderList();
    renderBalance();
  }

  // -----------------------------
  // Init
  // -----------------------------
  function init() {
    // fill selects
    fillSelect($("sn_node"), NODES);
    fillSelect($("sn_category"), CATEGORIES);

    fillSelect($("sn_filterNode"), NODES, { includeAll: true, allLabel: "All roles", allValue: "all" });
    fillSelect($("sn_filterCategory"), CATEGORIES, { includeAll: true, allLabel: "All categories", allValue: "all" });

    fillSelect($("sn_m_intent"), INTENTS);

    // load persisted state
    load();

    // apply persisted UI filters (after filling selects)
    if ($("sn_filterNode")) $("sn_filterNode").value = state.ui.filterNode || "all";
    if ($("sn_filterCategory")) $("sn_filterCategory").value = state.ui.filterCategory || "all";

    // listeners: add
    $("sn_add")?.addEventListener("click", addPerson);
    $("sn_clear")?.addEventListener("click", clearAddForm);

    // listeners: filters
    $("sn_search")?.addEventListener("input", (e) => {
      state.ui.search = e.target.value;
      save();
      renderList();
    });
    $("sn_filterNode")?.addEventListener("change", (e) => {
      state.ui.filterNode = e.target.value;
      save();
      renderList();
    });
    $("sn_filterCategory")?.addEventListener("change", (e) => {
      state.ui.filterCategory = e.target.value;
      save();
      renderList();
    });
    $("sn_sort")?.addEventListener("change", (e) => {
      state.ui.sort = e.target.value;
      save();
      renderList();
    });

    // modal listeners
    $("sn_m_close")?.addEventListener("click", closeModal);
    $("sn_modal")?.addEventListener("click", (e) => {
      // click outside card closes
      if (e.target && e.target.id === "sn_modal") closeModal();
    });

    $("sn_m_log")?.addEventListener("click", logInteraction);
    $("sn_m_saveReflect")?.addEventListener("click", saveReflection);
    $("sn_m_saveNext")?.addEventListener("click", saveNextAction);
    $("sn_m_delete")?.addEventListener("click", deletePerson);
    $("sn_m_copy")?.addEventListener("click", copyPersonSummary);

    // quarterly, export, reset
    $("sn_quarterly")?.addEventListener("click", generateQuarterlyReview);
    $("sn_export")?.addEventListener("click", exportJSON);
    $("sn_reset")?.addEventListener("click", resetAll);

    // first render
    render();

    // if previously selected modal, restore
    if (state.selectedId) {
      const p = getPerson(state.selectedId);
      if (p) {
        renderModal(p);
        setModalOpen(true);
      } else {
        state.selectedId = null;
        save();
      }
    }

    // nice default
    if ($("sn_energy") && !$("sn_energy").value) $("sn_energy").value = "energizing";
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
